/**
 * Wish jar helpers (MONETIZATION-PLAN §1.4 / §5.4, P0-4, live config `wishJar`): a child tapping locked
 * content (path node with status 'premium', playground item, locked world, story chapter, skin) goes to
 * the WISH screen — no price, no lock/crown — instead of the paywall.
 *
 *   nav(wishPath('node', miniUnitId, '/learn'))
 *   wishInfo(kind, id) → what to show (drawing / world name / skin) for the child and the grown-ups corner
 */
import type { Drawing, L, PathId, WishEntry, WishKind } from '@sketchling/core';
import { useApp } from '../../state/store';
import { CONTENT, IX, drawingById } from '../../lib/content';

export const SKIN_TITLES: Record<string, L> = {
  storybook: { en: 'Storybook', vi: 'Sách truyện' },
  sticker: { en: 'Bright stickers', vi: 'Sticker rực rỡ' },
  pastel: { en: 'Soft pastel', vi: 'Pastel dịu dàng' },
};

/** Route of the wish screen for a locked thing; `back` = where "Back" returns. */
export function wishPath(kind: WishKind, id: string, back?: string): string {
  const st = useApp.getState();
  if (st.config.wishJar === false) return `/paywall?from=wish${back ? `&next=${encodeURIComponent(back)}` : ''}`;
  return `/wish/${kind}/${encodeURIComponent(id)}${back ? `?back=${encodeURIComponent(back)}` : ''}`;
}

export interface WishInfo {
  title: L | string;
  drawing?: Drawing;
  world?: PathId;
}

export function wishInfo(kind: WishKind | string, id: string): WishInfo {
  if (kind === 'node' || kind === 'playground') {
    const mu = IX.miniUnits.get(id);
    const pg = IX.playground.get(id) ?? IX.playgroundByMiniUnit.get(id);
    const d = drawingById(mu?.drawingId ?? pg?.drawingId ?? '');
    if (d) return { title: d.title, drawing: d };
    const node = IX.pathNodes.get(id);
    if (node) return { title: node.path.title, world: node.path.id };
  }
  if (kind === 'world') {
    const p = IX.paths.get(id as PathId);
    if (p) return { title: p.title, world: p.id, drawing: drawingById(p.heroDrawingId) };
  }
  if (kind === 'story') {
    const ep = CONTENT.stories.find((s) => s.id === id);
    if (ep) return { title: ep.title };
    const p = IX.paths.get(id.split(':')[0] as PathId);
    if (p) return { title: p.title, world: p.id, drawing: drawingById(p.heroDrawingId) };
  }
  if (kind === 'skin') return { title: SKIN_TITLES[id] ?? id };
  return { title: { en: 'Something new', vi: 'Điều mới' } };
}

/** Wishes sorted newest first (derive inside useMemo; never in a selector). */
export const newestFirst = (w: readonly WishEntry[]) => [...w].sort((a, b) => (a.at < b.at ? 1 : -1));
